


































































































import {Component, Prop, Vue} from 'vue-property-decorator'
import {PropType} from 'vue'
import {$} from '@/facade'
import {NeoHelper} from '@/helpers/NeoHelper'
import {OrbitStartRoundStep} from '@/enums/OrbitStartRoundStep'
import {OrbitProject} from '@/model/resource/OrbitProject'
import {getN3InvocationBuilder} from '@/model/wallets/invocationsBuilder'
import OrbitLoadingTransactionStep from '@/components/orbit/OrbitLoadingTransactionStep.vue'
import OrbitInsufficientGasStep from '@/components/orbit/OrbitInsufficientGasStep.vue'
import OrbitUnexpectedErrorStep from '@/components/orbit/OrbitUnexpectedErrorStep.vue'
import OrbitRejectedByTheUserStep from '@/components/orbit/OrbitRejectedByTheUserStep.vue'
import OrbitSuccessStep from '@/components/orbit/OrbitSuccessStep.vue'
import {OrbitRound} from '@/model/resource/OrbitRound'
import {OrbitRoundCollection} from '@/model/collection/OrbitRoundCollection'
import {OrbitHelper} from '@/helpers/OrbitHelper'
import {OrbitNetworkHelper} from '@/helpers/OrbitNetworkHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'
import {DateHelper} from '@/helpers/DateHelper'

@Component({
  computed: {
    OrbitStartRoundStep() {
      return OrbitStartRoundStep
    },
  },
  components: {
    OrbitSuccessStep,
    OrbitLoadingTransactionStep,
    OrbitInsufficientGasStep,
    OrbitUnexpectedErrorStep,
    OrbitRejectedByTheUserStep,
  },
})
export default class OrbitStartRoundModal extends Vue {
  @Prop({type: Array as PropType<OrbitProject[] | null>}) orbitProjects!:
    | OrbitProject[]
    | null

  currentStep: OrbitStartRoundStep = OrbitStartRoundStep.WAITING_SIGNATURE
  transactionHash: string | null = null

  mounted() {
    this.populateResources()
  }

  async populateResources() {
    await OrbitHelper.checkWalletPermissions().catch(() => {
      this.closeModal()
      return
    })

    await this.startRound()
  }

  async startRound() {
    try {
      if (!this.orbitProjects || this.orbitProjects.length === 0) {
        throw new Error('Orbit projects not found')
      }

      const signingNetwork = currentN3Network()

      const n3InvocationBuilder = getN3InvocationBuilder()
      const startOrbitRoundInvocationBuilder = n3InvocationBuilder.startOrbitRoundInvocation(
        {
          projectInstances: this.orbitProjects.map(op => {
            if (!op.contract || !op.contract.hash || !op.instanceId) {
              throw new Error('Missing parameters')
            }

            if (
              op.blockchainVersion?.networkIdentifier?.trim().toLowerCase() !==
              signingNetwork
            ) {
              throw new Error(
                `Orbit project ${op.contract.hash} does not belong to ${signingNetwork}`
              )
            }

            return {
              scriptHash: op.contract.hash,
              instanceId: String(op.instanceId),
            }
          }),
        }
      )

      this.transactionHash = await $.walletAdapter.invokeN3Wallet(
        startOrbitRoundInvocationBuilder
      )
      if (!this.transactionHash) throw new Error('Undefined transaction')

      this.currentStep = OrbitStartRoundStep.VALIDATING_TRANSACTION

      const isTransactionValid = await NeoHelper.validateTransaction(
        this.transactionHash
      )
      if (!isTransactionValid) throw new Error('Failed to verify transaction')

      const lastRound = await OrbitHelper.getLastRound()
      const cfg = await OrbitHelper.resolveOrbitConfig()

      const currentRound = Number(lastRound) + 1

      const now = Date.now()
      const activePhaseEndMs = now + cfg.roundDuration
      const phases = OrbitHelper.phaseEndTimesFromActiveEndMs(
        activePhaseEndMs,
        cfg
      )

      const existingRound = await this.findExistingRound(
        currentRound,
        signingNetwork
      )
      const orbitRound: OrbitRound = existingRound || new OrbitRound()
      // Round numbers restart per network, so the round is only identifiable with it.
      orbitRound.blockchainVersion = await OrbitNetworkHelper.blockchainVersionOf(
        signingNetwork
      )
      orbitRound.round = currentRound
      orbitRound.isRunning = true
      orbitRound.startsAt = DateHelper.formatDateForBackend(now) as any
      orbitRound.endsAt = DateHelper.formatDateForBackend(
        activePhaseEndMs
      ) as any
      orbitRound.claimEndsAt = DateHelper.formatDateForBackend(
        phases.claimEndMs
      ) as any
      orbitRound.distributionEndsAt = DateHelper.formatDateForBackend(
        phases.distributionEndMs
      ) as any
      orbitRound.finishedAt = DateHelper.formatDateForBackend(
        phases.finishedMs
      ) as any
      orbitRound.transactionHash = this.transactionHash

      if (!existingRound) {
        orbitRound.createdAt = DateHelper.formatDateForBackend(now) as any
      }

      orbitRound.idOrbitRoundPk = await orbitRound.persistOrbitRound()

      await Promise.all(
        this.orbitProjects.map(async op => {
          op.orbitRound = orbitRound

          await op.persistOrbitProject()
        })
      )

      this.currentStep = OrbitStartRoundStep.STARTED

      $.toast.success('system.success.persist')
    } catch (error) {
      this.threatError(error)
    }

    this.closeModal()
  }

  private async findExistingRound(
    round: number,
    networkIdentifier: string
  ): Promise<OrbitRound | null> {
    try {
      const collection = new OrbitRoundCollection()
      collection.round = round
      collection.networkIdentifier = networkIdentifier
      collection.limit = 999
      collection.perPage = 999
      await collection.queryAsPage()

      return (
        (collection.items || []).find(item => Number(item.round) === round) ||
        null
      )
    } catch (error) {
      console.error('Failed to look up an existing Orbit round:', error)
      return null
    }
  }

  onClickCloseModal() {
    if (
      this.currentStep === OrbitStartRoundStep.WAITING_SIGNATURE ||
      this.currentStep === OrbitStartRoundStep.VALIDATING_TRANSACTION
    ) {
      return
    }

    this.closeModal()
  }

  closeModal() {
    this.currentStep = OrbitStartRoundStep.WAITING_SIGNATURE
    this.$emit('close')
  }

  private threatError(error: any) {
    console.error(error)

    if (
      String(error.message).includes('project instance already used in a round')
    ) {
      $.toast.error('Project already used in a round.')
    }

    const isNeonWalletRejected =
      error?.message && String(error.message).includes('rejected by the user')

    const isNeoLineWalletRejected =
      error?.description &&
      error.description === 'The user cancels, or refuses the dapps request'

    if (isNeonWalletRejected || isNeoLineWalletRejected) {
      this.currentStep = OrbitStartRoundStep.REJECTED_BY_THE_USER
      return
    }

    const hasInsufficientFunds =
      error?.message && String(error.message).includes('insufficient funds')
    if (hasInsufficientFunds) {
      this.currentStep = OrbitStartRoundStep.INSUFFICIENT_GAS
      return
    }

    this.currentStep = OrbitStartRoundStep.UNKNOWN_ERROR
  }
}
